/** Placeholder / format for typed birthday (DD-MM-YYYY). */
export const EMBED_BIRTHDAY_FORMAT_HINT = "DD-MM-YYYY";

/** Format digits as DD-MM-YYYY while typing (max 8 digits). */
export function formatEmbedBirthdayValue(raw) {
    const digits = String(raw ?? "").replace(/\D/g, "").slice(0, 8);
    if (digits.length <= 2) {
        return digits;
    }
    if (digits.length <= 4) {
        return digits.slice(0, 2) + "-" + digits.slice(2);
    }
    return (
        digits.slice(0, 2) + "-" + digits.slice(2, 4) + "-" + digits.slice(4)
    );
}

/** @param {string} iso `YYYY-MM-DD` */
export function embedBirthdayIsoToDisplay(iso) {
    const m = String(iso ?? "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) {
        return "";
    }
    return m[3] + "-" + m[2] + "-" + m[1];
}

/** @param {string} display `DD-MM-YYYY` */
export function embedBirthdayDisplayToIso(display) {
    const m = String(display ?? "").match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (!m) {
        return "";
    }
    return m[3] + "-" + m[2] + "-" + m[1];
}

/** @param {HTMLInputElement} el text input */
export function attachEmbedBirthdayFieldGuards(el) {
    el.addEventListener("keydown", function (ev) {
        if (ev.key === "e" || ev.key === "E") {
            ev.preventDefault();
        }
    });
    el.addEventListener("paste", function (ev) {
        ev.preventDefault();
        let text = "";
        try {
            text =
                (ev.clipboardData || window.clipboardData).getData("text") ||
                "";
        } catch (e) {
            /* ignore */
        }
        const start = el.selectionStart;
        const end = el.selectionEnd;
        if (typeof start !== "number" || typeof end !== "number") {
            el.value = formatEmbedBirthdayValue(text);
            return;
        }
        const next = formatEmbedBirthdayValue(
            el.value.slice(0, start) + text + el.value.slice(end)
        );
        el.value = next;
        el.setSelectionRange(next.length, next.length);
    });
    el.addEventListener("input", function () {
        const next = formatEmbedBirthdayValue(el.value);
        if (next !== el.value) {
            el.value = next;
        }
    });
}

/**
 * @param {HTMLInputElement} textInput
 * @param {HTMLInputElement} pickerInput `type="date"`
 * @param {HTMLButtonElement} pickerBtn
 */
export function attachEmbedBirthdayPickerSync(textInput, pickerInput, pickerBtn) {
    const syncPickerFromText = () => {
        const iso = embedBirthdayDisplayToIso(textInput.value);
        pickerInput.value = iso || "";
    };

    pickerBtn.addEventListener("click", function () {
        syncPickerFromText();
        if (typeof pickerInput.showPicker === "function") {
            try {
                pickerInput.showPicker();
                return;
            } catch (e) {
                /* fall through */
            }
        }
        pickerInput.focus();
    });

    pickerInput.addEventListener("change", function () {
        if (pickerInput.value) {
            textInput.value = embedBirthdayIsoToDisplay(pickerInput.value);
        }
    });

    textInput.addEventListener("change", syncPickerFromText);
    textInput.addEventListener("blur", syncPickerFromText);
}

const CALENDAR_ICON_SVG =
    '<svg class="claritick-ticket-embed-birthday-picker-btn__icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>';

/**
 * @param {Document} [doc]
 * @param {{ required?: boolean }} [opts]
 * @returns {{ wrap: HTMLDivElement, input: HTMLInputElement, picker: HTMLInputElement }}
 */
export function createEmbedBirthdayInputElement(doc, opts = {}) {
    const d = doc || document;
    const wrap = d.createElement("div");
    wrap.className = "claritick-ticket-embed-birthday-wrap";

    const textInput = d.createElement("input");
    textInput.type = "text";
    textInput.inputMode = "numeric";
    textInput.autocomplete = "bday";
    textInput.placeholder = EMBED_BIRTHDAY_FORMAT_HINT;
    textInput.maxLength = 10;
    textInput.setAttribute("pattern", "\\d{2}-\\d{2}-\\d{4}");
    textInput.title = "Enter date as DD-MM-YYYY or use the calendar";
    if (opts.required) {
        textInput.required = true;
    }

    const pickerBtn = d.createElement("button");
    pickerBtn.type = "button";
    pickerBtn.className = "claritick-ticket-embed-birthday-picker-btn";
    pickerBtn.setAttribute("aria-label", "Open calendar");
    pickerBtn.innerHTML = CALENDAR_ICON_SVG;

    const pickerInput = d.createElement("input");
    pickerInput.type = "date";
    pickerInput.lang = "en-GB";
    pickerInput.tabIndex = -1;
    pickerInput.className = "claritick-ticket-embed-birthday-picker-native";
    pickerInput.setAttribute("aria-hidden", "true");

    attachEmbedBirthdayFieldGuards(textInput);
    attachEmbedBirthdayPickerSync(textInput, pickerInput, pickerBtn);

    wrap.appendChild(textInput);
    wrap.appendChild(pickerBtn);
    wrap.appendChild(pickerInput);

    return { wrap, input: textInput, picker: pickerInput };
}
